import { withAlpha } from "@/lib/color";
import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COMPETITION_META } from "@/lib/competitions";

// Section definitions: each card is a self-contained table for a logical group.
// We define explicit column orders + optional sub-blocks (e.g. legacy national leagues).
const SECTIONS = [
    {
        key: "divisioni",
        title: "Divisioni",
        accent: "rgb(var(--tc-ffd700))",
        icon: "🏅",
        blocks: [
            {
                label: "Formato attuale (stagioni 13–25)",
                cols: ["league_top", "league_dop", "league_ops", "league_hope"],
                seasonRange: [13, 99],
            },
            {
                label: "Formato storico (stagioni 1–12)",
                cols: ["league_serie_a", "league_liga", "league_premier", "league_ligue1"],
                seasonRange: [1, 12],
            },
        ],
    },
    {
        key: "x_championship",
        title: "X-Championship",
        accent: "rgb(var(--tc-9c27b0))",
        icon: "⚔️",
        compact: true, // mostra solo stagioni con vincitore (o aggiunte da admin)
        blocks: [{ cols: ["x_championship"] }],
    },
    {
        key: "coppe_europee",
        title: "Coppe Europee",
        accent: "rgb(var(--tc-ffd700))",
        icon: "🌍",
        blocks: [{ cols: ["champions", "europa", "conference"] }],
    },
    {
        key: "coppe_nazionali",
        title: "Coppe Nazionali",
        accent: "rgb(var(--tc-2bb6ff))",
        icon: "🏆",
        blocks: [{ cols: ["coppa_italia", "copa_del_rey", "coppa_francia", "fa_cup"] }],
    },
    {
        key: "supercoppe",
        title: "Supercoppe",
        accent: "rgb(var(--tc-ff7a1a))",
        icon: "✨",
        blocks: [{
            cols: [
                "supercoppa_europea",
                "supercoppa_italiana",
                "supercoppa_francia",
                "supercoppa_spagna",
                "community_shield",
            ],
        }],
    },
    {
        key: "primavera",
        title: "Primavera",
        accent: "rgb(var(--tc-e8ff00))",
        icon: "🌱",
        compact: true,
        blocks: [{ cols: ["primavera"] }],
    },
    {
        key: "europei",
        title: "Nazionali (Europei)",
        accent: "rgb(var(--tc-00bcd4))",
        icon: "🌐",
        blocks: [{ cols: ["europei"], seasonRange: [19, 99] }],
    },
    {
        key: "premi_individuali",
        title: "Premi Individuali",
        accent: "rgb(var(--tc-ffd700))",
        icon: "🎖️",
        compact: true, // mostra solo stagioni con un premio assegnato (o aggiunte da admin)
        blocks: [{ cols: ["pallone_oro", "scarpa_oro", "premio_yashin"] }],
    },
];

function EditableCell({ entry, season, comp, isAdmin, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(entry?.winner || "");
    const [saving, setSaving] = useState(false);

    useEffect(() => { setValue(entry?.winner || ""); }, [entry?.winner]);

    const cellTestId = `albo-cell-${season}-${comp.key}`;

    const save = async () => {
        const v = value.trim();
        if (v === (entry?.winner || "")) { setEditing(false); return; }
        setSaving(true);
        try {
            if (!v) {
                if (entry?.id) await api.delete(`/albo/entries/${entry.id}`);
            } else if (entry?.id) {
                await api.patch(`/albo/entries/${entry.id}`, { winner: v });
            } else {
                await api.post(`/albo/entries`, {
                    season, competition_key: comp.key, winner: v,
                });
            }
            await onSaved();
            setEditing(false);
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio");
            setValue(entry?.winner || "");
        } finally { setSaving(false); }
    };

    if (editing) {
        return (
            <td className="px-1 py-2 text-center bg-t0e1014">
                <input
                    autoFocus
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onBlur={save}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") save();
                        if (e.key === "Escape") {
                            setValue(entry?.winner || ""); setEditing(false);
                        }
                    }}
                    disabled={saving}
                    className="w-full bg-t16181c border border-t2bb6ff text-tffffff text-xs px-2 py-1.5 focus:outline-none uppercase tracking-wide font-semibold text-center"
                    data-testid={`${cellTestId}-input`}
                />
            </td>
        );
    }

    const display = entry?.winner_display || entry?.winner || "";
    return (
        <td
            className={`px-3 py-2 text-center text-xs uppercase tracking-wide whitespace-nowrap font-semibold transition-colors ${
                isAdmin ? "cursor-pointer hover:bg-t2bb6ff/10" : ""
            } ${display ? "text-tffffff" : "text-t3a3d44"}`}
            onClick={() => isAdmin && setEditing(true)}
            title={isAdmin ? "Click per modificare" : ""}
            data-testid={cellTestId}
        >
            {display || (isAdmin ? "+" : "—")}
        </td>
    );
}

function AdminSeasonAdder({ nextSeason, onAdd }) {
    const [value, setValue] = useState(String(nextSeason));
    useEffect(() => { setValue(String(nextSeason)); }, [nextSeason]);
    const submit = (e) => {
        e?.preventDefault();
        const n = parseInt(value, 10);
        if (Number.isFinite(n) && n >= 1 && n <= 999) {
            onAdd(n);
        }
    };
    return (
        <form
            onSubmit={submit}
            className="flex items-center gap-3 mt-6 flex-wrap"
            data-testid="albo-admin-controls"
        >
            <label className="op-label text-[10px] text-tff7a1a">
                AGGIUNGI STAGIONE N°
            </label>
            <input
                type="number"
                min="1"
                max="999"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="op-input op-mono text-sm w-24 text-center"
                data-testid="albo-add-season-input"
            />
            <button
                type="submit"
                className="op-btn op-btn-primary text-sm"
                data-testid="albo-add-season-btn"
            >
                + Aggiungi
            </button>
            <span className="op-label text-[10px] text-t5c6370">
                Crea la riga in ogni sezione; poi clicca le celle per inserire i vincitori
            </span>
        </form>
    );
}

function SectionTable({ section, comps, byCell, seasons, extraSeasons, isAdmin, onSaved }) {
    return (
        <div
            className="op-card overflow-hidden mb-8"
            style={{ borderTop: `3px solid ${section.accent}` }}
            data-testid={`albo-section-${section.key}`}
        >
            <div className="px-5 py-4 border-b border-t22252a bg-t0e1014 flex items-center gap-3">
                <span className="text-2xl" style={{ filter: `drop-shadow(0 0 6px ${withAlpha(section.accent, "66")})` }}>
                    {section.icon}
                </span>
                <div>
                    <div className="op-label text-[10px]" style={{ color: section.accent }}>
                        // SEZIONE
                    </div>
                    <div className="op-heading text-tffffff text-xl tracking-wide">
                        {section.title}
                    </div>
                </div>
            </div>

            {section.blocks.map((block, bidx) => {
                let blockSeasons = seasons.filter((s) => {
                    if (!block.seasonRange) return true;
                    const [min, max] = block.seasonRange;
                    return s >= min && s <= max;
                });
                // Compact mode: solo stagioni con almeno un vincitore nelle colonne del blocco
                // (oppure stagioni aggiunte manualmente dall'admin, così può inserire un mancante)
                if (section.compact) {
                    const extraSet = new Set(extraSeasons || []);
                    blockSeasons = blockSeasons.filter((s) => {
                        if (extraSet.has(s)) return true;
                        return block.cols.some((ck) => byCell[s]?.[ck]?.winner);
                    });
                }
                if (blockSeasons.length === 0) {
                    return (
                        <div key={bidx} className="px-5 py-6 op-mono text-xs text-t5c6370 italic" data-testid={`albo-empty-${section.key}`}>
                            {isAdmin
                                ? 'Nessuna stagione con premio. Usa "+ Aggiungi" in alto per creare la riga, poi clicca le celle per assegnare i premi.'
                                : "Premi non ancora assegnati."}
                        </div>
                    );
                }
                return (
                    <div key={bidx} className={bidx > 0 ? "border-t border-t22252a" : ""}>
                        {block.label && (
                            <div className="px-5 py-2 op-label text-[10px] text-t8f95a3 bg-t0a0b0e">
                                {block.label}
                            </div>
                        )}

                        {/* MOBILE: una mini-tabella per ogni competizione, in colonna */}
                        <div className="sm:hidden divide-y divide-t22252a">
                            {block.cols.map((ck) => {
                                const c = comps[ck];
                                if (!c) return null;
                                const meta = COMPETITION_META[ck];
                                return (
                                    <div key={ck} className="p-3" data-testid={`albo-mobile-comp-${ck}`}>
                                        <div
                                            className="flex items-center gap-2.5 mb-2 pb-2 border-b border-t22252a"
                                            style={{ color: c.color }}
                                        >
                                            {meta?.image && (
                                                <img
                                                    src={meta.image}
                                                    alt=""
                                                    loading="lazy"
                                                    className="h-9 w-9 object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]"
                                                    style={{ filter: `drop-shadow(0 0 4px ${withAlpha(c.color, "88")})` }}
                                                />
                                            )}
                                            <div className="op-label text-[11px]">
                                                {c.label}
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            {blockSeasons.map((season) => {
                                                const entry = byCell[season]?.[ck];
                                                if (!entry?.winner && !isAdmin) return null;
                                                return (
                                                    <div
                                                        key={season}
                                                        className="flex items-center gap-3 text-xs"
                                                        data-testid={`albo-mobile-row-${ck}-${season}`}
                                                    >
                                                        <span className="op-mono font-bold text-t2bb6ff w-10 shrink-0">
                                                            S{season}
                                                        </span>
                                                        <table className="flex-1 min-w-0">
                                                            <tbody>
                                                                <tr>
                                                                    <EditableCell
                                                                        entry={entry}
                                                                        season={season}
                                                                        comp={c}
                                                                        isAdmin={isAdmin}
                                                                        onSaved={onSaved}
                                                                    />
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                );
                                            })}
                                            {blockSeasons.every((s) => !byCell[s]?.[ck]?.winner) && !isAdmin && (
                                                <div className="op-mono text-[10px] text-t5c6370 italic">
                                                    Nessun vincitore registrato
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* DESKTOP: tabella multi-colonna come prima */}
                        <div className="hidden sm:block overflow-x-auto">
                            <table className="min-w-full text-xs">
                                <thead>
                                    <tr className="bg-t0a0b0e border-b border-t22252a">
                                        <th className="px-3 py-3 text-left op-label text-[10px] text-tff7a1a sticky left-0 bg-t0a0b0e z-10 border-r border-t22252a" style={{ minWidth: 70 }}>
                                            Stagione
                                        </th>
                                        {block.cols.map((ck) => {
                                            const c = comps[ck];
                                            if (!c) return null;
                                            const meta = COMPETITION_META[ck];
                                            return (
                                                <th
                                                    key={ck}
                                                    className="px-3 py-3 text-center op-label text-[10px] whitespace-nowrap align-bottom"
                                                    style={{ color: c.color, minWidth: 130 }}
                                                >
                                                    <div className="flex flex-col items-center gap-1.5">
                                                        {meta?.image ? (
                                                            <img
                                                                src={meta.image}
                                                                alt=""
                                                                loading="lazy"
                                                                className="h-12 w-12 object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.55)]"
                                                                style={{ filter: `drop-shadow(0 0 6px ${withAlpha(c.color, "99")})` }}
                                                            />
                                                        ) : (
                                                            <div
                                                                className="h-12 w-12 flex items-center justify-center op-heading text-base rounded-full border"
                                                                style={{ color: c.color, borderColor: `${withAlpha(c.color, "55")}`, background: `${withAlpha(c.color, "11")}` }}
                                                            >
                                                                {c.label.charAt(0)}
                                                            </div>
                                                        )}
                                                        <span>{c.label}</span>
                                                    </div>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {blockSeasons.map((season, idx) => (
                                        <tr
                                            key={season}
                                            className={`border-b border-t16181c ${
                                                idx % 2 === 0 ? "bg-t0a0b0e" : "bg-t0d0e12"
                                            } hover:bg-t16181c/60`}
                                            data-testid={`albo-row-${section.key}-${season}`}
                                        >
                                            <td className="px-3 py-2 op-mono text-sm font-bold text-t2bb6ff sticky left-0 bg-inherit z-10 border-r border-t22252a">
                                                S{season}
                                            </td>
                                            {block.cols.map((ck) => {
                                                const c = comps[ck];
                                                if (!c) return <td key={ck} />;
                                                return (
                                                    <EditableCell
                                                        key={ck}
                                                        entry={byCell[season]?.[ck]}
                                                        season={season}
                                                        comp={c}
                                                        isAdmin={isAdmin}
                                                        onSaved={onSaved}
                                                    />
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default function Albo() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";

    const [competitions, setCompetitions] = useState([]);
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [extraSeasons, setExtraSeasons] = useState([]);

    const load = async () => {
        try {
            const [comps, ents] = await Promise.all([
                api.get("/albo/competitions"),
                api.get("/albo/entries"),
            ]);
            setCompetitions(comps.data || []);
            setEntries(ents.data || []);
        } catch (e) {
            console.error(e);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const compsMap = useMemo(() => {
        const m = {};
        for (const c of competitions) m[c.key] = c;
        return m;
    }, [competitions]);

    const byCell = useMemo(() => {
        const m = {};
        for (const e of entries) {
            if (!m[e.season]) m[e.season] = {};
            m[e.season][e.competition_key] = e;
        }
        return m;
    }, [entries]);

    // Seasons = max(23, max(entry.season), max(extraSeasons)) down to 1
    const seasons = useMemo(() => {
        let maxS = 23;
        for (const e of entries) if (e.season > maxS) maxS = e.season;
        for (const s of extraSeasons) if (s > maxS) maxS = s;
        return Array.from({ length: maxS }, (_, i) => maxS - i);
    }, [entries, extraSeasons]);

    const nextSeason = useMemo(() => {
        let maxS = 23;
        for (const e of entries) if (e.season > maxS) maxS = e.season;
        for (const s of extraSeasons) if (s > maxS) maxS = s;
        return maxS + 1;
    }, [entries, extraSeasons]);

    const addSeason = (n) => {
        const num = parseInt(n, 10);
        if (!Number.isFinite(num) || num < 1 || num > 999) return;
        setExtraSeasons((arr) => (arr.includes(num) ? arr : [...arr, num]));
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="albo-page">
            <div className="op-label" style={{ color: "rgb(var(--tc-ff7a1a))" }}>// STORIA DEL TORNEO</div>
            <h1 className="op-heading text-5xl sm:text-6xl mt-3">
                <span className="op-gradient-text">ALBO D'ORO</span>
            </h1>
            <p className="text-t8f95a3 text-sm mt-3 max-w-3xl">
                Tutti i vincitori di ogni competizione, stagione per stagione.
                {isAdmin && (
                    <span className="block mt-2 text-t2bb6ff">
                        Sei admin: clicca su qualsiasi cella per modificarla (Invio per salvare, Esc per annullare, vuoto per cancellare).
                    </span>
                )}
            </p>

            {isAdmin && (
                <AdminSeasonAdder
                    nextSeason={nextSeason}
                    onAdd={addSeason}
                />
            )}

            {loading ? (
                <div className="op-label text-t8f95a3 animate-pulse mt-10">// CARICAMENTO…</div>
            ) : (
                <div className="mt-8">
                    {SECTIONS.map((section) => (
                        <SectionTable
                            key={section.key}
                            section={section}
                            comps={compsMap}
                            byCell={byCell}
                            seasons={seasons}
                            extraSeasons={extraSeasons}
                            isAdmin={isAdmin}
                            onSaved={load}
                        />
                    ))}
                </div>
            )}

            <div className="mt-4 text-[11px] text-t5c6370 op-mono">
                {entries.length} record · {competitions.length} competizioni · {seasons.length} stagioni
            </div>
        </div>
    );
}
